import {
  type FieldValues,
  type UseControllerProps,
  useController,
} from 'react-hook-form';
import { NumberInput } from 'shared/ui/Inputs/NumberInput/NumberInput';
import type { Props } from '../Inputs/NumberInput';

export type NumberFieldProps<T extends FieldValues> = UseControllerProps<T> &
  Omit<Props, 'value' | 'defaultValue'>;

/**
 * NumberField component
 *
 * @example
 * ```tsx
 * <NumberField name="field.name" required />
 * ```
 *
 * @public
 * @param name - field name
 * @param control - form control
 * @param defaultValue - default value
 * @param rules - validation rules
 * @param shouldUnregister - unregister field when unmount
 * @param onChange - onChange event
 * @param props - other props
 */
export function NumberField<T extends FieldValues>({
  name,
  control,
  defaultValue,
  rules,
  shouldUnregister,
  onChange,
  ...props
}: NumberFieldProps<T>) {
  const {
    field: { value, onChange: fieldOnChange, ...field },
    fieldState,
  } = useController<T>({
    name,
    control,
    defaultValue,
    rules,
    shouldUnregister,
  });

  return (
    <NumberInput
      error={fieldState.error?.message}
      value={value}
      onChange={e => {
        fieldOnChange(e);
        onChange?.(e);
      }}
      {...field}
      {...props}
    />
  );
}
